'use client';

import { useEffect } from 'react';

/**
 * Global handler for ChunkLoadError that occurs after deployments.
 *
 * When a new version is deployed, chunk hashes change but users may have
 * cached HTML referencing old chunks. This component detects the error
 * and forces a full page reload to get fresh assets.
 *
 * Storage key prevents infinite reload loops if the error persists.
 */
export function ChunkErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const error = event.error;

      // Check for ChunkLoadError (webpack/Next.js chunk loading failure)
      if (
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('Failed to load chunk')
      ) {
        const reloadKey = 'chunk-error-reload';
        const lastReload = sessionStorage.getItem(reloadKey);
        const now = Date.now();

        // Prevent infinite reload loops - only reload once per 30 seconds
        if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
          sessionStorage.setItem(reloadKey, now.toString());

          // Force a hard reload to get fresh assets from server
          window.location.reload();
        }
      }
    };

    // Handle unhandled promise rejections (dynamic imports throw these)
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;

      if (
        reason?.name === 'ChunkLoadError' ||
        reason?.message?.includes('Loading chunk') ||
        reason?.message?.includes('Failed to load chunk')
      ) {
        const reloadKey = 'chunk-error-reload';
        const lastReload = sessionStorage.getItem(reloadKey);
        const now = Date.now();

        if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
          sessionStorage.setItem(reloadKey, now.toString());
          window.location.reload();
        }
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
